import { useState } from 'react';
import { RplClient, RplDTO, ApiResponse_string, UpdateRplStatusDTO, ForfeitureRequestDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

export const useRplApi = () => {
    const apiClient = new RplClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: RplDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createRpl(data);
        } catch (err: any) {
            setError(err.message || 'Error creating Rpl');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: RplDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateRpl(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating Rpl');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getRplById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching Rpl');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchRpls(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching Rpls');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteRpl(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting Rpl');
        } finally {
            setIsLoading(false);
        }
    };

    const restoreFromDeleted = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.restoreFromDeletedRpl(id);
        } catch (err: any) {
            setError(err.message || 'Error restoring Rpl from deleted state');
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportRpls(filters);
        } catch (err: unknown) {
            handleHookError(err, 'Error exporting Rpls', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateProofOfPayment = async (id: string, fileRecordId: string) => {
        setIsLoading(true);
        try {
            await apiClient.updateRplProofOfPayment(id, fileRecordId);
        } catch (err: any) {
            setError(err.message || 'Error updating proof of payment');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const sendProofOfPaymentLink = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.sendRplProofOfPaymentLink(id);
        } catch (err: any) {
            setError(err.message || 'Error updating proof of payment');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getFeedback = async (id: string): Promise<string | undefined> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getRplFeedback(id);
            return response.data ?? undefined;
        } catch (err: any) {
            setError(err.message || 'Error fetching Rpl feedback');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateStatus = async (dto: UpdateRplStatusDTO) => {
        setIsLoading(true);
        try {
            return await apiClient.updateRplQuickStatus(dto);
        } catch (err: any) {
            setError(err.message || 'Error updating rpl status');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const forfeit = async (id: string, data: ForfeitureRequestDTO) => {
        setIsLoading(true);
        try {
            return await apiClient.forfeitRpl(id, data);
        } catch (err: any) {
            handleHookError(err, 'Error forfeiting RPL', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getForfeitureDocument = async (id: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await (apiClient as any).getRplForfeitureDocument(id);
            if (response.success && response.data) {
                return response.data;
            }
            throw new Error(response.message || 'Failed to get forfeiture document URL');
        } catch (err: any) {
            handleHookError(err, 'Error getting forfeiture document', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, search, extract, remove, restoreFromDeleted, updateProofOfPayment, sendProofOfPaymentLink, updateStatus, forfeit, getForfeitureDocument, getFeedback, isLoading, error };
};